import attractionIcon from 'assets/poi/attraction.svg';
import collegeIcon from 'assets/poi/college.svg';
import eventIcon from 'assets/poi/event.svg';
import hotelIcon from 'assets/poi/hotel.svg';
import moviesIcon from 'assets/poi/movies.svg';
import museumIcon from 'assets/poi/museum.svg';
import restaurantIcon from 'assets/poi/restaurant.svg';
import shoppingIcon from 'assets/poi/shopping.svg';
import stadiumIcon from 'assets/poi/stadium.svg';

/**
 * Demand-generator points of interest (the reasons people come to, and park in, an area),
 * drawn from the Mapbox Streets basemap's poi_label tiles. Icons and category rules are
 * shared with the parking-coverage-mapper tool so both maps read the same way.
 */

export interface PoiCategory {
    key: string;
    label: string;
    icon: string;
}

export const POI_CATEGORIES: PoiCategory[] = [
    { key: 'poi-restaurant', label: 'Dining & Nightlife', icon: restaurantIcon },
    { key: 'poi-stadium', label: 'Sports Venue', icon: stadiumIcon },
    { key: 'poi-event', label: 'Live Performance', icon: eventIcon },
    { key: 'poi-movies', label: 'Movie Theater', icon: moviesIcon },
    { key: 'poi-museum', label: 'Museum or Gallery', icon: museumIcon },
    { key: 'poi-attraction', label: 'Attraction', icon: attractionIcon },
    { key: 'poi-shopping', label: 'Shopping', icon: shoppingIcon },
    { key: 'poi-college', label: 'College', icon: collegeIcon },
    { key: 'poi-hotel', label: 'Hotel', icon: hotelIcon },
];

export const POI_LAYER_ID = 'ag-poi-venues';

// maki groupings the basemap's arts_and_entertainment class splits into.
const MUSEUM_MAKI = ['museum', 'art-gallery'];
const ATTRACTION_MAKI = ['attraction', 'monument', 'viewpoint', 'zoo', 'aquarium', 'amusement-park'];
const PERFORMANCE_MAKI = ['theatre', 'music'];
// Large retail destinations only, so the map isn't flooded with corner stores.
const SHOPPING_MAKI = ['mall', 'department-store', 'marketplace'];

type PoiProperties = { class?: string; maki?: string; name?: string };

/** Which badge a basemap POI feature gets, or null when it is not a demand generator. */
export function poiCategoryKey(props: PoiProperties): string | null {
    const maki = props.maki || '';
    if (maki === 'stadium') return 'poi-stadium';
    if (maki === 'cinema') return 'poi-movies';
    if (PERFORMANCE_MAKI.includes(maki)) return 'poi-event';
    if (MUSEUM_MAKI.includes(maki)) return 'poi-museum';
    if (ATTRACTION_MAKI.includes(maki)) return 'poi-attraction';
    if (SHOPPING_MAKI.includes(maki)) return 'poi-shopping';
    if (maki === 'college') return 'poi-college';
    if (maki === 'lodging') return 'poi-hotel';
    if (props.class === 'food_and_drink') return 'poi-restaurant';
    return null;
}

export function poiCategoryLabel(key: string | null): string {
    const category = POI_CATEGORIES.find((c) => c.key === key);
    return category ? category.label : 'Point of Interest';
}

const POI_FILTER = [
    'any',
    ['==', ['get', 'class'], 'food_and_drink'],
    ['==', ['get', 'maki'], 'stadium'],
    ['==', ['get', 'maki'], 'cinema'],
    ['in', ['get', 'maki'], ['literal', PERFORMANCE_MAKI]],
    ['in', ['get', 'maki'], ['literal', MUSEUM_MAKI]],
    ['in', ['get', 'maki'], ['literal', ATTRACTION_MAKI]],
    ['in', ['get', 'maki'], ['literal', SHOPPING_MAKI]],
    ['==', ['get', 'maki'], 'college'],
    ['==', ['get', 'maki'], 'lodging'],
];

const POI_ICON_IMAGE = [
    'case',
    ['==', ['get', 'maki'], 'stadium'],
    'poi-stadium',
    ['==', ['get', 'maki'], 'cinema'],
    'poi-movies',
    ['in', ['get', 'maki'], ['literal', PERFORMANCE_MAKI]],
    'poi-event',
    ['in', ['get', 'maki'], ['literal', MUSEUM_MAKI]],
    'poi-museum',
    ['in', ['get', 'maki'], ['literal', ATTRACTION_MAKI]],
    'poi-attraction',
    ['in', ['get', 'maki'], ['literal', SHOPPING_MAKI]],
    'poi-shopping',
    ['==', ['get', 'maki'], 'college'],
    'poi-college',
    ['==', ['get', 'maki'], 'lodging'],
    'poi-hotel',
    'poi-restaurant',
];

/** False once mapbox-gl's remove() has torn the map down (its style is gone, so getLayer would throw). */
function isMapUsable(map: any): boolean {
    return Boolean(map && map.style);
}

/** Register the SVG badges as map images (rasterized at 2x for retina). Best-effort per icon. */
function loadPoiIcons(map: any): Promise<void[]> {
    return Promise.all(
        POI_CATEGORIES.map(
            ({ key, icon }) =>
                new Promise<void>((resolve) => {
                    if (map.hasImage(key)) return resolve();
                    const image = new Image(64, 64);
                    image.onload = () => {
                        try {
                            if (!map.hasImage(key)) map.addImage(key, image, { pixelRatio: 2 });
                        } catch (e) {
                            // Another call registered it first.
                        }
                        resolve();
                    };
                    image.onerror = () => resolve();
                    image.src = icon;
                })
        )
    );
}

const inflightEnsures = new WeakMap<object, Promise<boolean>>();

/** Add the hidden POI symbol layer once the style has loaded. Resolves false if the basemap lacks poi_label. */
export function ensurePoiLayer(map: any): Promise<boolean> {
    const inflight = inflightEnsures.get(map);
    if (inflight) return inflight;
    const promise = addPoiLayer(map).finally(() => inflightEnsures.delete(map));
    inflightEnsures.set(map, promise);
    return promise;
}

async function addPoiLayer(map: any): Promise<boolean> {
    if (!isMapUsable(map)) return false;
    if (map.getLayer(POI_LAYER_ID)) return true;
    // poi_label lives in the Streets "composite" source.
    if (!map.getSource('composite')) return false;
    await loadPoiIcons(map);
    // The map may have been removed while the icons were loading.
    if (!isMapUsable(map)) return false;
    if (map.getLayer(POI_LAYER_ID)) return true;
    map.addLayer({
        id: POI_LAYER_ID,
        type: 'symbol',
        source: 'composite',
        'source-layer': 'poi_label',
        filter: POI_FILTER,
        layout: {
            visibility: 'none',
            'icon-image': POI_ICON_IMAGE,
            'icon-size': 0.8,
            'icon-anchor': 'center',
            'icon-allow-overlap': false,
            // Name below the badge, fading in once zoomed in enough to read it.
            'text-field': ['get', 'name'],
            'text-anchor': 'top',
            'text-offset': [0, 0.9],
            'text-size': 11,
            'text-optional': true,
            'text-max-width': 9,
        },
        paint: {
            'text-color': '#21201c',
            'text-halo-color': '#ffffff',
            'text-halo-width': 1.4,
            'text-opacity': ['interpolate', ['linear'], ['zoom'], 14.5, 0, 15.5, 1],
        },
    });
    return true;
}

export function setPoiLayerVisible(map: any, visible: boolean) {
    if (!isMapUsable(map) || !map.getLayer(POI_LAYER_ID)) return;
    map.setLayoutProperty(POI_LAYER_ID, 'visibility', visible ? 'visible' : 'none');
}

export interface HoveredPoi {
    name: string;
    category: string;
    lng: number;
    lat: number;
}

/** Report the POI under the cursor (null when the cursor leaves the layer). */
export function bindPoiHover(map: any, onHover: (poi: HoveredPoi | null) => void) {
    map.on('mousemove', POI_LAYER_ID, (event: any) => {
        const feature = event.features && event.features[0];
        if (!feature || !feature.geometry) return;
        map.getCanvas().style.cursor = 'pointer';
        const props: PoiProperties = feature.properties || {};
        const [lng, lat] = feature.geometry.coordinates;
        onHover({
            name: props.name || 'Point of Interest',
            category: poiCategoryLabel(poiCategoryKey(props)),
            lng,
            lat,
        });
    });
    map.on('mouseleave', POI_LAYER_ID, () => {
        map.getCanvas().style.cursor = '';
        onHover(null);
    });
}
